/**
 * These Class is used in web views and in PowerPoint Export, that is the reason why it is in its own file
 */

import ISeason from '@app/types/data/base/ISeason';
import SeasonType from '@app/types/base/SeasonType';

interface INormalizedDateRange {
	label: string;
	normalizedWidth: number; // 0-1
}

export interface ILabelPosition {
	label: string;
	widthPercent: number;
	leftPercent: number;
}

export interface ISeasonPosition {
	type: SeasonType;
	widthPercent: number;
	leftPercent: number;
}

export interface IOptions {
	locale: string;
}

export class SeasonCalendarBaseClass {
	protected readonly seasons: ISeason[];

	protected readonly startDateRange?: string;

	protected readonly endDateRange?: string;

	private readonly options: IOptions;

	protected labelsWithPosition: ILabelPosition[] = [];

	protected seasonsWithPosition: ISeasonPosition[] = [];

	protected normalizedTotalRangeWidth: number = 0;

	public constructor(
		seasons: ISeason[],
		startDateRange?: string, // format MM-DD
		endDateRange?: string, // format MM-DD
		options: IOptions = { locale: 'en-US' }
	) {
		this.seasons = seasons;
		this.startDateRange = startDateRange;
		this.endDateRange = endDateRange;
		this.options = options;
		this.init();
	}

	private init(): void {
		this.labelsWithPosition.push(...this.getLabelsWithPosition());
		this.seasonsWithPosition.push(...this.getSeasonsWithPosition());
	}

	/**
	 * Calculates the width and left position of every single month in the base range in percent.
	 * It is used, to draw the base chart via inline styles in the template.
	 */
	public getLabelsWithPosition(): ILabelPosition[] {
		const { baseStartDate, baseEndDate } = this.getBaseDates();
		const labelsWithPosition: ILabelPosition[] = [];
		let accumulatedWidth = 0;

		const dateRange = this.getNormalizedDateRange(baseStartDate, baseEndDate);
		this.normalizedTotalRangeWidth = dateRange.reduce((sum, item) => sum + item.normalizedWidth, 0);

		dateRange.forEach((item): void => {
			const widthPercent = (item.normalizedWidth / this.normalizedTotalRangeWidth) * 100;
			const leftPercent = (accumulatedWidth / this.normalizedTotalRangeWidth) * 100;

			accumulatedWidth += item.normalizedWidth;

			labelsWithPosition.push({
				label: item.label,
				widthPercent,
				leftPercent
			});
		});

		return labelsWithPosition;
	}

	/**
	 * Calculates the width and left position of the seasons in percent, relative to the base chart.
	 * It is used, to draw the seasons in relation to the base chart via inline styles in the template.
	 */
	public getSeasonsWithPosition(): ISeasonPosition[] {
		const normalizedSeasons = this.getAdjustedSeasons();
		const seasonsWithPosition: ISeasonPosition[] = [];

		normalizedSeasons.forEach(season => {
			const { baseStartDate } = this.getBaseDates();

			const seasonStart = new Date(season.startDate);
			const seasonEnd = new Date(season.endDate);

			const widthDateRange = this.getNormalizedDateRange(seasonStart, seasonEnd);
			// subtract one day from seasonStart to avoid overlapping with the season
			seasonStart.setDate(seasonStart.getDate() - 1);
			const leftDateRange = this.getNormalizedDateRange(baseStartDate, seasonStart);

			const totalNormalizedWidth = widthDateRange.reduce((sum, item) => sum + item.normalizedWidth, 0);
			const totalNormalizedLeft = leftDateRange.reduce((sum, item) => sum + item.normalizedWidth, 0);

			const widthPercent = (totalNormalizedWidth / this.normalizedTotalRangeWidth) * 100;
			const leftPercent = (totalNormalizedLeft / this.normalizedTotalRangeWidth) * 100;

			seasonsWithPosition.push({
				type: season.type,
				widthPercent,
				leftPercent
			});
		});

		return seasonsWithPosition;
	}

	/**
	 * Returns An array of the months within the given range, with labels and a	normalized value.
	 * The normalized value is a number between 0 and 1 representing the width of the passed days, in proportion to
	 * the total number of days in the month. It will be easier later to get the width in percent for the chart.
	 */
	private getNormalizedDateRange(startDate: Date, endDate: Date): INormalizedDateRange[] {
		const { baseStartDate, baseEndDate } = this.getBaseDates();
		const months: INormalizedDateRange[] = [];

		let currentMonth = baseStartDate.getUTCMonth();
		let currentYear = baseStartDate.getUTCFullYear();
		const endMonth = baseEndDate.getUTCMonth();
		const endYear = baseEndDate.getUTCFullYear();

		const formatter = new Intl.DateTimeFormat(this.options.locale, {
			month: 'short',
			timeZone: 'UTC' // Erzwingt UTC
		});

		while (currentYear < endYear || (currentYear === endYear && currentMonth <= endMonth)) {
			const firstDayOfCurrentMonth = new Date(Date.UTC(currentYear, currentMonth, 1));
			const lastDayOfCurrentMonth = new Date(Date.UTC(currentYear, currentMonth + 1, 0));

			const start = startDate.getTime() > firstDayOfCurrentMonth.getTime() ? startDate : firstDayOfCurrentMonth;
			const end = endDate.getTime() < lastDayOfCurrentMonth.getTime() ? endDate : lastDayOfCurrentMonth;

			const daysInCurrentMonth = lastDayOfCurrentMonth.getUTCDate();
			const daysInSubRange = (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24) + 1;
			const normalizedWidth = daysInSubRange / daysInCurrentMonth;

			if (normalizedWidth > 0) {
				// client time zones behind UTC would lead to the previous month being displayed when using toLocaleString()
				// --> use Int.DateTimeFormat instead
				const label = formatter.format(new Date(Date.UTC(currentYear, currentMonth, 1)));
				months.push({ label, normalizedWidth });
			}

			currentMonth++;
			if (currentMonth > 11) {
				currentMonth = 0;
				currentYear++;
			}
		}

		return months;
	}

	/**
	 * If a date is out of range, it will be adjusted to fit into the base date range. Because of this, we have to
	 * split one affected season item into two if necessary. Now it's easier to place it visually in the chart.
	 */
	private getAdjustedSeasons(): ISeason[] {
		const { baseStartDate, baseEndDate } = this.getBaseDates();
		const alignedSeasons: ISeason[] = [];

		const toIsoString = (date: Date): string => date.toISOString().slice(0, 10);

		this.seasons.forEach(season => {
			// if no date range is given, use the full base range
			if (!season.startDate || !season.endDate) {
				alignedSeasons.push({
					...season,
					startDate: toIsoString(baseStartDate),
					endDate: toIsoString(baseEndDate)
				});
				return;
			}

			const seasonStartDate = new Date(season.startDate);
			const seasonEndDate = new Date(season.endDate);

			const baseStartTime = baseStartDate.getTime();
			const baseEndTime = baseEndDate.getTime();
			const seasonStartTime = seasonStartDate.getTime();
			const seasonEndTime = seasonEndDate.getTime();

			const isStartDateOutLeftRange = seasonStartTime < baseStartTime;
			const isStartDateOutRightRange = seasonStartTime > baseEndTime;
			const isStartDateInRange = seasonStartTime >= baseStartTime && seasonStartTime <= baseEndTime;
			const isEndDateOutOfLeftRange = seasonEndTime < baseStartTime;
			const isEndDateOutRightRange = seasonEndTime > baseEndTime;
			const isEndDateInRange = seasonEndTime >= baseStartTime && seasonEndTime <= baseEndTime;

			const isStartDateSameMonthThanBaseStart = seasonStartDate.getUTCMonth() === baseStartDate.getUTCMonth();
			const isEndDateSameMonthThanBaseEnd = seasonEndDate.getUTCMonth() === baseEndDate.getUTCMonth();

			if (isStartDateInRange && isEndDateOutRightRange) {
				alignedSeasons.push({
					...season,
					startDate: toIsoString(seasonStartDate),
					endDate: toIsoString(baseEndDate)
				});

				// next year, subtract one year from the end date, so it is visually correct in the timeline
				seasonEndDate.setUTCFullYear(seasonEndDate.getUTCFullYear() - 1);
				alignedSeasons.push({
					...season,
					startDate: toIsoString(baseStartDate),
					endDate: toIsoString(seasonEndDate)
				});
			}

			if (isStartDateOutLeftRange && isEndDateInRange) {
				// current year, add one year from to the start date, so it is visually correct in the timeline
				seasonStartDate.setUTCFullYear(seasonStartDate.getUTCFullYear() + 1);
				alignedSeasons.push({
					...season,
					startDate: toIsoString(seasonStartDate),
					endDate: toIsoString(baseEndDate)
				});

				alignedSeasons.push({
					...season,
					startDate: toIsoString(baseStartDate),
					endDate: toIsoString(seasonEndDate)
				});
			}

			// edge case for backwards compatibility with old cms data
			if (isEndDateOutOfLeftRange && isEndDateSameMonthThanBaseEnd) {
				seasonStartDate.setUTCFullYear(seasonStartDate.getUTCFullYear() + 1);
				seasonEndDate.setUTCFullYear(seasonEndDate.getUTCFullYear() + 1);

				alignedSeasons.push({
					...season,
					startDate: toIsoString(seasonStartDate),
					endDate: toIsoString(seasonEndDate)
				});
			}

			// edge case for backwards compatibility with old cms data
			if (isStartDateOutRightRange && isStartDateSameMonthThanBaseStart) {
				seasonStartDate.setUTCFullYear(seasonStartDate.getUTCFullYear() - 1);
				seasonEndDate.setUTCFullYear(seasonEndDate.getUTCFullYear() - 1);

				alignedSeasons.push({
					...season,
					startDate: toIsoString(seasonStartDate),
					endDate: toIsoString(seasonEndDate)
				});
			}

			if (isStartDateInRange && isEndDateInRange) {
				alignedSeasons.push({
					...season,
					startDate: toIsoString(seasonStartDate),
					endDate: toIsoString(seasonEndDate)
				});
			}
		});

		return alignedSeasons;
	}

	/**
	 * Returns the base start and end date for the chart range. If no date range is given, defaults are used.
	 * The year of the latest season start date is used to create the year for the base range. This way it will
	 * stay flexible for future seasons as the year has not to be passed explicitly.
	 */
	private getBaseDates(): { baseStartDate: Date; baseEndDate: Date } {
		const startDateYears = this.seasons
			.filter(season => season.startDate)
			.map(season => Number(new Date(season.startDate).toISOString().slice(0, 4)));

		const baseYear = Math.max(...startDateYears);

		const startRange = this.startDateRange && this.endDateRange ? this.startDateRange : '12-16';
		const endRange = this.startDateRange && this.endDateRange ? this.endDateRange : '12-15';

		const startRangeInt = Number(startRange.split('-').join(''));
		const endRangeInt = Number(endRange.split('-').join(''));
		const isEndLower = startRangeInt > endRangeInt;

		const baseStartDate = new Date(`${isEndLower ? baseYear - 1 : baseYear}-${startRange}`);
		const baseEndDate = new Date(`${baseYear}-${endRange}`);

		return { baseStartDate, baseEndDate };
	}
}
